import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  EventAvailable as EventAvailableIcon,
  Groups as GroupsIcon,
  Payments as PaymentsIcon,
  QrCodeScanner as QrCodeScannerIcon,
  TrendingUp as TrendingUpIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import type { DocumentData, QuerySnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useTenant } from '../providers/TenantProvider';
import { canAccessMenuFeature } from '../lib/planFeatures';
import {
  computeReportingSummary,
  defaultReportRange,
  formatCurrency,
  formatPercent,
  startOfDay,
  toDate,
  type ReportingAppointment,
  type ReportingCheckIn,
  type ReportingData,
  type ReportingInvoice,
  type ReportingLead,
  type ReportingMember,
} from '../lib/reporting';

interface StatCardProps {
  label: string;
  value: string;
  helper?: string;
  icon: ReactNode;
  tone?: 'default' | 'warning' | 'success';
}

function StatCard({ label, value, helper, icon, tone = 'default' }: StatCardProps) {
  const color = tone === 'warning' ? 'warning.main' : tone === 'success' ? 'success.main' : 'primary.main';

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box sx={{ color, pt: 0.5 }}>{icon}</Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography color="text.secondary" gutterBottom>
              {label}
            </Typography>
            <Typography variant="h3">{value}</Typography>
            {helper && (
              <Typography variant="caption" color="text.secondary">
                {helper}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function coerceCollection<T extends { id: string }>(snapshot: QuerySnapshot<DocumentData>) {
  return snapshot.docs.map((document) => ({
    id: document.id,
    ...document.data(),
  })) as T[];
}

function timestampLabel(value: unknown) {
  const date = toDate(value);
  return date ? date.toLocaleString() : 'Unknown time';
}

function sameDay(value: unknown, date: Date) {
  const next = toDate(value);
  if (!next) return false;
  return startOfDay(next).getTime() === startOfDay(date).getTime();
}

function leadDisplayName(lead: ReportingLead) {
  const legacyLead = lead as ReportingLead & {
    contact?: { firstName?: string; lastName?: string; email?: string; phone?: string };
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  const firstName = legacyLead.contact?.firstName || legacyLead.firstName || '';
  const lastName = legacyLead.contact?.lastName || legacyLead.lastName || '';
  const fallback = legacyLead.contact?.email || legacyLead.email || 'Unnamed lead';
  return [firstName, lastName].filter(Boolean).join(' ') || fallback;
}

function leadSecondaryText(lead: ReportingLead) {
  const legacyLead = lead as ReportingLead & {
    contact?: { email?: string; phone?: string };
    email?: string;
    phone?: string;
  };
  const email = legacyLead.contact?.email || legacyLead.email || 'No email';
  const phone = legacyLead.contact?.phone || legacyLead.phone || 'No phone';
  return `${email} • ${phone}`;
}

export const DashboardPage = () => {
  const { user, tenantId, role, activeTenant } = useTenant();
  const canUseLeads = canAccessMenuFeature(activeTenant?.planId, 'leads');
  const [data, setData] = useState<ReportingData>({
    members: [],
    leads: [],
    checkIns: [],
    invoices: [],
    appointments: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    async function loadData() {
      setLoading(true);
      setError(null);

      try {
        const tenantFilter = where('tenantId', '==', tenantId);
        const [membersSnap, leadsSnap, checkInsSnap, invoicesSnap, appointmentsSnap] = await Promise.all([
          getDocs(query(collection(db, 'members'), tenantFilter)),
          canUseLeads ? getDocs(query(collection(db, 'leads'), tenantFilter)) : Promise.resolve(null),
          getDocs(query(collection(db, 'checkins'), tenantFilter)),
          getDocs(query(collection(db, 'invoices'), tenantFilter)),
          getDocs(query(collection(db, 'appointments'), tenantFilter)),
        ]);

        setData({
          members: coerceCollection<ReportingMember>(membersSnap),
          leads: leadsSnap ? coerceCollection<ReportingLead>(leadsSnap) : [],
          checkIns: coerceCollection<ReportingCheckIn>(checkInsSnap),
          invoices: coerceCollection<ReportingInvoice>(invoicesSnap),
          appointments: coerceCollection<ReportingAppointment>(appointmentsSnap),
        });
      } catch (loadError: any) {
        console.error('Error loading dashboard data:', loadError);
        setError(loadError.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [canUseLeads, tenantId]);

  const range = useMemo(() => defaultReportRange(), []);
  const summary = useMemo(() => computeReportingSummary(data, range), [data, range]);
  const today = useMemo(() => new Date(), []);
  const todaysCheckIns = useMemo(
    () => data.checkIns.filter((checkIn) => sameDay(checkIn.timestamp || checkIn.createdAt, today)).length,
    [data.checkIns, today]
  );
  const todaysAppointments = useMemo(
    () => data.appointments.filter((appointment) => sameDay(appointment.startTime, today)),
    [data.appointments, today]
  );
  const recentLeads = useMemo(
    () =>
      [...data.leads]
        .sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0))
        .slice(0, 5),
    [data.leads]
  );
  const recentCheckIns = useMemo(
    () =>
      [...data.checkIns]
        .sort(
          (a, b) =>
            (toDate(b.timestamp || b.createdAt)?.getTime() || 0) -
            (toDate(a.timestamp || a.createdAt)?.getTime() || 0)
        )
        .slice(0, 8),
    [data.checkIns]
  );
  const memberLookup = useMemo(() => new Map(data.members.map((member) => [member.id, member])), [data.members]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tenantId) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
        <Container maxWidth="md">
          <Paper sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom color="error">
              Account Setup Required
            </Typography>
            <Typography variant="body1" paragraph>
              Your account does not have tenant access configured yet.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <Box component="strong" sx={{ fontWeight: 700 }}>
                User Email:
              </Box>{' '}
              {user?.email}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <Box component="strong" sx={{ fontWeight: 700 }}>
                User ID:
              </Box>{' '}
              {user?.uid}
            </Typography>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Quick Setup
            </Typography>
            <Typography variant="body2" paragraph>
              Go to signup to create or attach a workspace:
            </Typography>
            <Button variant="contained" color="primary" href="/signup" sx={{ mb: 3 }}>
              Open Signup
            </Button>
            <Typography variant="caption" display="block" color="text.secondary">
              Once the tenant exists, owners and staff can finish onboarding from inside the app.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Welcome back, {user?.email} ({role})
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                label="Revenue This Month"
                value={formatCurrency(summary.revenueCollected)}
                helper={`${formatCurrency(summary.scheduledRevenue)} scheduled`}
                icon={<PaymentsIcon />}
                tone="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                label="Overdue Balance"
                value={formatCurrency(summary.overdueBalance)}
                helper={`${summary.overdueInvoices.length} invoice${summary.overdueInvoices.length === 1 ? '' : 's'} need attention`}
                icon={<WarningAmberIcon />}
                tone={summary.overdueBalance > 0 ? 'warning' : 'default'}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                label="Active Members"
                value={summary.activeMembers.toLocaleString()}
                helper={`${summary.newMembers} joined this month`}
                icon={<GroupsIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                label="Today's Check-ins"
                value={todaysCheckIns.toLocaleString()}
                helper={`${summary.attendance} this month`}
                icon={<QrCodeScannerIcon />}
              />
            </Grid>
            {canUseLeads && (
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  label="New Leads"
                  value={summary.newLeads.toLocaleString()}
                  helper={`${formatPercent(summary.leadConversionRate)} won this month`}
                  icon={<TrendingUpIcon />}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                label="Today's Schedule"
                value={todaysAppointments.length.toLocaleString()}
                helper={`${summary.noShowAppointments} no-show this month`}
                icon={<EventAvailableIcon />}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {canUseLeads && (
              <Grid item xs={12} lg={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">Recent Leads</Typography>
                    <Button size="small" href="/leads">
                      View All
                    </Button>
                  </Stack>
                  <List>
                    {recentLeads.map((lead) => (
                      <ListItem key={lead.id} divider>
                        <ListItemText
                          primary={leadDisplayName(lead)}
                          secondary={
                            <>
                              {leadSecondaryText(lead)}
                              <Box component="span" sx={{ display: 'block' }}>
                                Source: {lead.source || 'Unknown'}
                              </Box>
                            </>
                          }
                        />
                        <Chip label={lead.pipelineStage || 'new'} size="small" />
                      </ListItem>
                    ))}
                    {recentLeads.length === 0 && (
                      <ListItem>
                        <ListItemText secondary="No leads found" />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6">Recent Check-ins</Typography>
                  <Button size="small" href="/checkin">
                    Open Check-In
                  </Button>
                </Stack>
                <List>
                  {recentCheckIns.map((checkIn) => {
                    const member = memberLookup.get((checkIn as ReportingCheckIn & { memberId?: string }).memberId || '');
                    const name = member?.contact
                      ? [member.contact.firstName, member.contact.lastName].filter(Boolean).join(' ')
                      : (checkIn as ReportingCheckIn & { memberId?: string }).memberId || 'Unknown member';

                    return (
                      <ListItem key={checkIn.id} divider>
                        <ListItemText
                          primary={name}
                          secondary={`${timestampLabel(checkIn.timestamp || checkIn.createdAt)} • Method: ${checkIn.method || 'unknown'}`}
                        />
                      </ListItem>
                    );
                  })}
                  {recentCheckIns.length === 0 && (
                    <ListItem>
                      <ListItemText secondary="No check-ins found" />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6">Billing Attention</Typography>
                  <Button size="small" href="/reports">
                    Open Reports
                  </Button>
                </Stack>
                <Grid container spacing={2}>
                  {summary.overdueInvoices.slice(0, 4).map((invoice) => (
                    <Grid item xs={12} md={6} lg={3} key={invoice.id}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" noWrap>
                          {invoice.id}
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(invoice.amountDue ?? invoice.total ?? 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Due {timestampLabel(invoice.dueDate)}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                  {summary.overdueInvoices.length === 0 && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        No overdue invoices found.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
};

export default DashboardPage;
